import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import translate from '../../utilities/translator';
import Button from '../../components/Button';
import DealsLoader from '../../components/Deals/DealsLoader';
import { getDateLabel, getNightsLabel } from '../../utilities/dealsHelper';
import { selectFilterData } from '../mainFilter/selectors';
import { isMobile } from '../../utilities/helpers';
import DealItemExperimental from '../dealsList/DealItemExperimental';
import NonCachedOffer from './NonCachedOffer';
import NonCachedCardsVirtualized from './NonCachedCardsVirtualized';
import MobileListVirtualized from '../dealsList/MobileListVirtualized';
// import abTesting from '../../services/abTesting';

export default function LocationDealsList({
  list,
  prices,
  onLoadMore,
  pagination,
  isSpecific: isSpecificDateSelected,
  hideSoldOutDeals,
  priceGraphTop,
  pending
}) {
  const when = useSelector((state) => selectFilterData(state).searchEngine.when);
  const selectedHotel = useSelector((state) => state.userSearch.selectedHotel);
  const [isFetching, setIsFetching] = useState(false);
  const { pingStatusPending, pending: pendingHotelPrices } = prices;
  const showLoader = (!isSpecificDateSelected && pagination.page < pagination.pages) ||
    (isSpecificDateSelected && prices.pagination?.total_offers_pages > prices.pagination?.current_offers_page);

  const scrollToRef = useRef();
  const getOffsetTopToFetch = () => {
    if (scrollToRef && scrollToRef.current) {
      return (scrollToRef.current.offsetTop - 500);
    }
    return (document.scrollingElement.scrollHeight - 500);
  }

  useEffect(() => {
    const handleScroll = () => {
      // infinite scroll for mobile only
      if (!isMobile()) { return; }
      if (showLoader && !isFetching && !prices.pending && !pending && window.innerHeight + document.documentElement.scrollTop >= getOffsetTopToFetch()) {
        setIsFetching(true);
      } else {
        return;
      }
    };

    if (isMobile()) { window.addEventListener('scroll', handleScroll); }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showLoader, prices.pending, pending]);

  useEffect(() => {
    if (isFetching && showLoader && !pending && !prices.pending) {
      onLoadMore();
      setIsFetching(false);
    }
  }, [isFetching, pending, prices.pending, showLoader]);

  useEffect(() => {
    if (!pendingHotelPrices && isSpecificDateSelected && prices.pagination?.total_offers_pages > prices.pagination?.current_offers_page && !prices.offers.length) {
      onLoadMore();
    }
  }, [pendingHotelPrices, isSpecificDateSelected, prices, prices.pagination, onLoadMore]);

  if (!when) return '';

  const dateLabel = getDateLabel(when);
  const nightsLabel = getNightsLabel(when);

  const getLocationState = (index) => {
    if (isSpecificDateSelected && !prices.error && prices.offers.length === 0)
      return 'pending';
    if (isSpecificDateSelected && !prices.error && pingStatusPending) return 'pending';
    if (isSpecificDateSelected && !pingStatusPending) {
      return getLocationPrice(index) ? 'available' : 'sold';
    }

    return 'default';
  };

  const getLocationPrice = (index) => {
    if (index <= 20) {
      const currentFlight = prices.offers.find(
        (item) => item.hotel.provider_hotel_id === list[index].id
      );
      if (currentFlight) {
        return currentFlight.offer.price;
      }

      return null;
    }

    return prices[index].offer.price;
  };

  const getFilteredDealsWithPrices = () => {
    const excludedOfferIds = new Set(list.slice(0, 20).map(offer => offer.id));

    const filteredOffers = prices.offers.filter(({ hotel }) => {
      // Filter out offers already in the top 20 list or selected hotel
      return !excludedOfferIds.has(hotel.provider_hotel_id) &&
        (!selectedHotel || hotel.provider_hotel_id !== selectedHotel.hotelId);
    });

    return filteredOffers
  }

  const renderPrices = () => {
    const deals = getFilteredDealsWithPrices();

    if (isMobile()) {
      return (
        <MobileListVirtualized
          dealsArray={deals}
          isMultipleDestination={false}
          filtersInteractionCount={0}
          isSpecificDates={isSpecificDateSelected}
          selectedHotel={selectedHotel}
          bestOutOfMultiplied={[]}
          dealOfTheDay={[]}
          dotdPending={false}
        />
      )
    }

    return getFilteredDealsWithPrices().map((offer, index) => {
      if (
        selectedHotel && selectedHotel.hotelId &&
        offer.hotel && offer.hotel.provider_hotel_id &&
        selectedHotel.hotelId === offer.hotel.provider_hotel_id
      ) {
        // do not show selected (highlighted) hotel for the second time
        return null;
      }

      return (
        <DealItemExperimental
          key={offer.offer.offerId}
          dealItem={offer}
          animated={false}
          hideSettings
          position={index}
        />
      );
    });
  };

  const renderAnimatedPrices = () => {
    let animatedList = list.slice(0, 20);

    return animatedList.map((hotelItem, index) => {
      const dealItem = prices.offers.find(
        (item) => item.hotel.provider_hotel_id === hotelItem.id
      );
      if (selectedHotel && selectedHotel.hotelId && hotelItem.id === selectedHotel.hotelId) {
        // do not show selected (highlighted) hotel for the second time
        return null;
      }

      if (hotelItem.hotelPhotos) {
        if (dealItem) {
          // CachedCard
          return (
            <DealItemExperimental
              key={dealItem.offer.offerId}
              dealItem={dealItem}
              hideSettings
              position={index}
            />
          );
        }

        // NonCachedCard
        return (
          <NonCachedOffer
            key={hotelItem.id + index}
            hotelItem={hotelItem}
            dateLabel={dateLabel}
            nightsLabel={nightsLabel}
            state={getLocationState(index)}
            price={getLocationPrice(index)}
            photos={hotelItem.hotelPhotos}
            hotelName={hotelItem.text}
            dealItem={dealItem}
            hideSoldOutDeals={hideSoldOutDeals}
            index={index}
          />
        );
      }

      return null;
    });
  };

  const renderNotSpecificDateSelected = () => {
    const filteredList = list.filter(hotelItem => hotelItem.hotelPhotos);

    return isMobile() ? (
      <NonCachedCardsVirtualized
        deals={filteredList.filter(hotelItem => hotelItem.hotelPhotos)}
        dateLabel={dateLabel}
        nightsLabel={nightsLabel}
      />
    ) : (
      filteredList.map((hotelItem, index) => (
        <NonCachedOffer
          key={hotelItem.id + index}
          hotelItem={hotelItem}
          dateLabel={dateLabel}
          nightsLabel={nightsLabel}
          state="default"
          photos={hotelItem.hotelPhotos}
          hotelName={hotelItem.text}
          index={index}
        />
      ))
    )
  }

  const componentBody = (
    <>
      {!isSpecificDateSelected && renderNotSpecificDateSelected()}

      {isSpecificDateSelected && (
        <>
          {renderAnimatedPrices()}
          {renderPrices()}
        </>
      )}

      {(isFetching || (prices.pending && isMobile())) && (
        <DealsLoader />
      )}

      {showLoader && !pingStatusPending && isMobile() && (
        <div ref={scrollToRef} className="scrollto-block" />
      )}

      {showLoader && !isMobile() && (
        <div className="text-center">
          <Button
            type="secondary"
            size="big"
            label={translate('holidayFinder.dealItem.load_more')}
            onClick={onLoadMore}
            disabled={pending || pingStatusPending || pendingHotelPrices}
            icon={(pingStatusPending || pendingHotelPrices) && <i className="ri-loader-2-line load-ico" />}
          />
        </div>
      )}
    </>
  );

  if (priceGraphTop) {
    return componentBody;
  }

  return (
    <>
      {componentBody}
    </>
  )
}
